import { matchPath } from "react-router-dom";
import { routePolicyDefaults, type AppRoutePolicy } from "@/components/layout/AppRoutePolicyContext";

export type RoutePolicyDescriptor = {
  /** Route pattern (supports exact and wildcard patterns compatible with React Router). */
  path: string;
  /** Route policy for the matching path. */
  policy: AppRoutePolicy;
  /** Explicit exemption for route-level error UI; used by config verification only. */
  exemptFromRouteErrorBoundary?: boolean;
  /** Inline review note used when route intentionally opts out of the default policy path. */
  policyNote?: string;
};

export const ROUTE_POLICY_CONFIG: RoutePolicyDescriptor[] = [
  {
    path: "/services",
    policy: {
      suppressTopBarQuickAdd: true,
      routeRecoveryHint: "This route exposes a page-level Quick Add action in the page header.",
      errorRecovery: "default",
      timeoutMs: 0,
    },
    policyNote: "Suppress shell Quick Add because page-level header provides a dedicated control. Timeout disabled – services routes manage their own loading states via Suspense.",
  },
  {
    path: "/member",
    policy: {
      showTopBarQuickAdd: false,
      errorRecovery: "default",
      timeoutMs: 0,
    },
    policyNote: "Member shell uses a compact shell context and suppresses redundant top actions. Timeout disabled – member pages manage their own loading states via Suspense/skeleton UI.",
  },
  {
    path: "/engagement",
    policy: {
      timeoutMs: 16_000,
      errorRecovery: "default",
      routeRecoveryHint:
        "Engagement dashboards load multiple live modules; if data never resolves, retry or return home.",
    },
    policyNote: "Engagement dashboard can be query-heavy, so include a route-level timeout hint.",
  },
  {
    path: "/public/*",
    policy: {
      showTopBarQuickAdd: false,
      timeoutMs: 0,
      errorRecovery: "default",
      routeRecoveryHint: "Public routes do not rely on authenticated shell controls.",
    },
    policyNote:
      "Public routes skip Quick Add and use a lighter error recovery expectation. Timeout disabled – public/donor pages have no auth dependency and run on slow mobile networks; genuinely stuck lazy loads are covered by ForgePageLoader's stall recovery UI (Retry) and lazyWithRetry, so an artificial route timeout only risks replacing a rendered donor page (UX audit bug 1.1).",
  },
];

const DEFAULT_ROUTE_POLICY: AppRoutePolicy = {
  ...routePolicyDefaults,
  exemptFromRouteErrorBoundary: false,
};

export const resolveRoutePolicy = (pathname: string): AppRoutePolicy => {
  const exactMatch = ROUTE_POLICY_CONFIG.find(({ path }) => matchPath({ path, end: false }, pathname));

  return {
    ...DEFAULT_ROUTE_POLICY,
    ...(exactMatch?.policy ?? {}),
  };
};

export const isRouteErrorBoundaryExempt = (pathname: string): boolean => {
  const exactMatch = ROUTE_POLICY_CONFIG.find(({ path }) => matchPath({ path, end: false }, pathname));
  return exactMatch?.exemptFromRouteErrorBoundary ?? false;
};
